
  import { Component, Prop, Watch } from 'vue-property-decorator'
  import { GForm } from '@/components/forms/GForm'
  import { removeAccents } from '@/utils/general'
  import { Generation } from '@/entities/vehicle'
  import { plainToInstance } from 'class-transformer'
  import { Debounce } from '@/utils/decorators'
  import _ from 'lodash'

@Component({
  components: { },
})
  export default class ImportAttribute extends GForm {
  @Prop({ type: Object, default: () => null }) value:any
  @Prop({ type: Boolean, default: false }) hiddenOne!: boolean

  disabled=true
  generations: Generation[] = []
  selected = null

  @Watch('value', { immediate: true, deep: true })
  async onValueChange (newVal: any) {
    // Verificar validaciones iniciales
    const localValue = this.validateAndPrepare(newVal)
    if (!localValue) return

    try {
      const generation = await this.fetchGenerations(localValue)

      if (generation?.length > 1) {
        await this.handleMultipleGenerations(generation, localValue)
      } else if (generation?.length === 1) {
        await this.handleSingleGeneration(generation)
      } else {
        await this.handleNoGenerations(localValue)
      }
    } catch (error) {
      console.error(`[ImportAttribute] ERROR durante procesamiento: ${error}`)
    }

    // Emitir cambios al componente padre
    this.emitValue()
  }

  private validateAndPrepare (newVal: any) {
    // Validación adicional para depuración
    if (!newVal) {
      return null
    }

    if (!newVal.version?.match?.id) {
      return null
    }

    if (!newVal.checkAttributes) {
      return null
    }

    // Creamos copia local para evitar modificar valores compartidos
    const localValue = _.cloneDeep(newVal)
    this.generations = []

    // Desactivar checkAttributes para evitar múltiples actualizaciones
    localValue.checkAttributes = false

    // Usar Vue.set para garantizar reactividad
    this.$set(newVal, 'checkAttributes', false)

    // Manejo explícito del caso en que idGeneration pertenezca a otra versión
    if (localValue.idGeneration && localValue.idVersionYear && localValue.idVersionYear !== localValue.version.match.id) {
      this.$set(this.value, 'idGeneration', null)
    }

    return localValue
  }

  private async fetchGenerations (localValue: any) {
    const generation = await this.fetchData({
      query: { name: 'findLite', model: 'Generation' },
      filter: {
        id_version_year: { _eq: localValue.version.match.id },
      },
    })

    // Si no hay generaciones, limpiar explícitamente el idGeneration
    if (!generation || generation.length === 0) {
      this.$set(this.value, 'idGeneration', null)
    }

    this.generations = generation

    return generation
  }

  private async handleMultipleGenerations (generation: any[], localValue: any) {
    const generationMatch = this.findMatchingGeneration(generation, localValue)

    if (generationMatch?.id) {
      this.setGenerationValues(_.cloneDeep(generationMatch))
    }

    this.disabled = false
  }

  private findMatchingGeneration (generation: any[], localValue: any) {
    return generation.find((item: any) => {
      let match = true

      const newValTraction = localValue?.traction?.val?.length
        ? localValue?.traction?.val
        : localValue?.traction?.value?.length
          ? localValue?.traction?.value
          : null

      const newValFuel = localValue?.fuel?.val?.length
        ? localValue?.fuel?.val
        : localValue?.fuel?.value?.length
          ? localValue?.fuel?.value
          : null

      const newValTransmission = localValue?.transmission?.val?.length
        ? localValue?.transmission?.val
        : localValue?.transmission?.value?.length
          ? localValue?.transmission?.value
          : null

      if (item.traction.val?.length && removeAccents(item.traction.val?.toLowerCase()) !== removeAccents(newValTraction?.toLowerCase())) {
        match = false
      }

      if (item.fuel.val?.length && removeAccents(item.fuel.val?.toLowerCase()) !== removeAccents(newValFuel?.toLowerCase())) {
        match = false
      }

      if (item.transmission.val?.length && removeAccents(item.transmission.val?.toLowerCase()) !== removeAccents(newValTransmission?.toLowerCase())) {
        match = false
      }

      return match
    })
  }

  private async handleSingleGeneration (generation: any[]) {
    const singleGen = _.cloneDeep(generation[0])

    this.setGenerationValues(singleGen)
    this.generations = [singleGen]
  }

  private async handleNoGenerations (localValue: any) {
    const attributes = await this.fetchData({
      query: {
        name: 'find',
        model: 'Attribute',
      },
      filter: {
        _and: [
          { id_version_year: { _eq: localValue.version.match.id } },
          { component: { slug: { _in: ['fuel', 'transmission', 'traction'] } } },
        ],
      },
    })

    // Solo actualizamos el objeto local, no el compartido
    const tractionValue = attributes.find((item: any) => item.slug === 'traction')?.val
    const fuelValue = attributes.find((item: any) => item.slug === 'fuel')?.val
    const transmissionValue = attributes.find((item: any) => item.slug === 'transmission')?.val

    // Creamos una copia de los objetos para evitar referencias compartidas
    const tractionObj = _.cloneDeep(this.value.traction)
    const fuelObj = _.cloneDeep(this.value.fuel)
    const transmissionObj = _.cloneDeep(this.value.transmission)

    tractionObj.value = tractionValue
    fuelObj.value = fuelValue
    transmissionObj.value = transmissionValue

    const customGeneration = plainToInstance(Generation, {
      id: null,
      sku: 'Sin Codigo',
      traction: tractionObj,
      fuel: fuelObj,
      transmission: transmissionObj,
    })

    this.generations = [customGeneration]
    this.selected = customGeneration

    // Obtener el ID de la versión
    const versionId = localValue.version.match.id

    // Actualizar valores con Vue.set para garantizar reactividad
    this.$set(this.value, 'traction', _.cloneDeep(tractionObj))
    this.$set(this.value, 'fuel', _.cloneDeep(fuelObj))
    this.$set(this.value, 'transmission', _.cloneDeep(transmissionObj))
    this.$set(this.value, 'generation', _.cloneDeep(customGeneration))

    // Eliminar idGeneration ya que no hay generación válida
    this.$set(this.value, 'idGeneration', null)

    // Establecer idVersion e idVersionYear al ID de la versión seleccionada
    this.$set(this.value, 'idVersion', versionId)
    this.$set(this.value, 'idVersionYear', versionId)
  }

  setGenerationValues (generation) {
      if (!generation.sku) {
        generation.sku = 'Sin Código'
      }

      // Obtener el ID de la versión seleccionada
      const versionId = this.value.version.match.id

      // Actualizar los valores de atributos si existen
      if (generation?.traction?.val) {
        this.$set(this.value.traction, 'value', generation.traction.val)
      }

      if (generation?.fuel?.val) {
        this.$set(this.value.fuel, 'value', generation.fuel.val)
      }

      if (generation?.transmission?.val) {
        this.$set(this.value.transmission, 'value', generation.transmission.val)
      }

      // Actualizar la generación
      this.$set(this.value, 'generation', _.cloneDeep(generation))

      // Establecer idGeneration solo si la generación tiene un ID válido
      if (generation.id) {
        this.$set(this.value, 'idGeneration', generation.id)
      } else {
        this.$set(this.value, 'idGeneration', null)
      }

      // Asegurar que idVersion e idVersionYear se actualicen correctamente
      this.$set(this.value, 'idVersion', versionId)
      this.$set(this.value, 'idVersionYear', versionId)

      this.selected = generation
  }

  onAttributesChange (selected) {
    if (!selected) {
      // Si se deselecciona, limpiar la generación pero mantener idVersion
      this.$set(this.value, 'idGeneration', null)

      // Asegurarse de que idVersion se mantenga establecido correctamente
      if (this.value.version && this.value.version.match && this.value.version.match.id) {
        const versionId = this.value.version.match.id
        this.$set(this.value, 'idVersion', versionId)
        this.$set(this.value, 'idVersionYear', versionId)
      }

      this.emitValue()
      return
    }

    // Si la generación seleccionada no tiene un ID válido, establecer idGeneration a null
    if (!selected.id) {
      this.$set(this.value, 'idGeneration', null)

      // Pero mantener idVersion actualizado
      if (this.value.version && this.value.version.match && this.value.version.match.id) {
        const versionId = this.value.version.match.id
        this.$set(this.value, 'idVersion', versionId)
        this.$set(this.value, 'idVersionYear', versionId)
      }
    }

    this.setGenerationValues(_.cloneDeep(selected))
  }

  @Debounce(500)
  emitValue () {
    // Asegurarse de que idVersion e idVersionYear siempre estén sincronizados antes de emitir
    if (this.value?.version?.match?.id) {
      const versionId = this.value.version.match.id

      // Actualizar idVersion con el valor de idVersionYear
      if (this.value.idVersionYear !== versionId) {
        this.$set(this.value, 'idVersionYear', versionId)
      }

      // Asegurar que idVersion siempre tenga el mismo valor que idVersionYear
      this.$set(this.value, 'idVersion', versionId)
    }

    // Emitir una copia profunda del valor
    this.$emit('input', _.cloneDeep(this.value))
  }

  get displayField () {
    const { disabled, hiddenOne, generations } = this

    if (generations.length > 1) return true
    if (hiddenOne && disabled) return false
    return true
  }
  }
